import { useProductContext } from "./ProductContext/ProductContext";
import type { MergedProduct } from "./ProductContext/merge";
import { useSuiteState } from "./SuiteState/SuiteState";
import { SKU, SuiteState, computePricing, deriveAutoAdds } from "./engine";

export const serializeSuiteContents = (
    suite: SuiteState,
    catalog: Map<SKU, MergedProduct>,
    locale: string,
    hasCmsData: boolean,
): string | null => {
    if (suite.contributions.length === 0) return null;

    const lookup = (sku: SKU) => {
        if (!hasCmsData) return { sku };
        const entry = catalog.get(sku);
        return {
            sku,
            name: entry?.name ?? sku,
            price: entry?.msrp.formatted ?? null,
        };
    };

    const pricing = hasCmsData ? computePricing(suite, catalog, locale) : null;

    return JSON.stringify({
        contributions: suite.contributions.map((c) => ({
            spoke: c.spoke,
            main: lookup(c.mainSku),
            accessories: c.accessorySkus.map(lookup),
        })),
        autoAdds: deriveAutoAdds(suite)
            .filter((a) => a.count > 0)
            .map((a) => ({ ...lookup(a.sku), count: a.count })),
        ...(pricing && {
            totals: {
                subtotal: pricing.subtotal.formatted,
                savings: pricing.savings.formatted,
                total: pricing.total.formatted,
            },
        }),
    });
};

export const useSuiteContents = (): string | null => {
    const { catalog, locale, hasCmsData } = useProductContext();
    const { value: suite } = useSuiteState();
    return serializeSuiteContents(suite, catalog, locale, hasCmsData);
};
